import { useAgentDelegation } from "../hooks";
import {
  useConnect,
  useDisconnect,
  useAccount,
  useSignTypedData,
  useChainId,
} from "wagmi";
import { useEffect, useState } from "react";

function AgentDelegation(): JSX.Element {
  const {
    ownerAddress,
    setOwnerAddress,
    agentAddress,
    setAgentAddress,
    agentPrivateKey,
    expiresInDays,
    setExpiresInDays,
    permissions,
    setPermissions,
    agentName,
    setAgentName,
    nonce,
    setNonce,
    result,
    loading,
    generateAgentKeypair,
    prepareAgentSign,
    isMetaMaskConnected,
    isPhantomConnected,
    isOneKeyConnected,
    isReady,
  } = useAgentDelegation();

  const { connect, connectors, isPending: isConnecting } = useConnect();
  const { disconnect } = useDisconnect();
  const { address: wagmiAddress, isConnected: wagmiIsConnected } = useAccount();
  const { signTypedDataAsync } = useSignTypedData();
  const chainId = useChainId();

  // State to store available addresses from wallet connection
  const [availableAddresses, setAvailableAddresses] = useState<string[]>([]);
  // Local state for signing result (to avoid WASM dependency)
  const [signingResult, setSigningResult] = useState<{
    success: boolean;
    signature?: string;
    typedData?: any;
    error?: string;
  } | null>(null);
  const [isSigning, setIsSigning] = useState(false);

  // Helper function to prepare EIP-712 typed data without WASM
  const prepareTypedDataForSigning = () => {
    if (!ownerAddress || !agentAddress) {
      throw new Error("Owner and agent addresses are required");
    }

    const permissionsArray = permissions
      .split(",")
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
    const deadline =
      Math.floor(Date.now() / 1000) + expiresInDays * 24 * 60 * 60;

    // Standard EIP-712 structure for agent approval
    const domain = {
      name: "Morpheum",
      version: "",
      chainId: chainId,
      verifyingContract:
        "0x0000000000000000000000000000000000000000" as `0x${string}`, // Placeholder
    };

    const types = {
      EIP712Domain: [
        { name: "name", type: "string" },
        { name: "version", type: "string" },
        { name: "chainId", type: "uint256" },
        { name: "verifyingContract", type: "address" },
      ],
      AgentApproval: [
        { name: "owner", type: "address" },
        { name: "agent", type: "address" },
        { name: "agentName", type: "string" },
        { name: "expiresInDays", type: "uint256" },
        { name: "permissions", type: "string[]" },
        { name: "nonce", type: "string" },
        { name: "deadline", type: "uint256" },
      ],
    };

    const message = {
      owner: ownerAddress.toLowerCase(),
      agent: agentAddress.toLowerCase(),
      agentName: agentName || "",
      expiresInDays: expiresInDays,
      permissions: permissionsArray,
      nonce: nonce || Date.now().toString(),
      deadline: deadline,
    };

    return {
      domain,
      types,
      primaryType: "AgentApproval" as const,
      message,
    };
  };

  // Direct signing function that doesn't use WASM
  const handleSignAgentApproval = async () => {
    if (!ownerAddress || !agentAddress) {
      setSigningResult({
        success: false,
        error: "Owner and agent addresses are required",
      });
      return;
    }

    setIsSigning(true);
    setSigningResult(null);

    try {
      // Prepare typed data directly (no WASM)
      const typedData = prepareTypedDataForSigning();

      // Sign with wallet (this will pop the wallet dialog)
      // wagmi expects the typed data in a specific format
      const signature = await signTypedDataAsync({
        domain: typedData.domain as any,
        types: typedData.types as any,
        primaryType: typedData.primaryType,
        message: typedData.message as any,
      });

      // Set result with signature and typed data
      setSigningResult({
        success: true,
        signature: signature,
        typedData: typedData,
      });
    } catch (error: any) {
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected")
      ) {
        setSigningResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        console.error("Error signing:", error);
        setSigningResult({
          success: false,
          error: error.message || "Unknown error during signing",
        });
      }
    } finally {
      setIsSigning(false);
    }
  };

  // Check if wallets are available (convert to boolean)
  const isMetaMaskAvailable =
    typeof window !== "undefined" && !!(window as any).ethereum?.isMetaMask;

  const isPhantomAvailable =
    typeof window !== "undefined" &&
    (!!(window as any).phantom?.ethereum ||
      !!(window as any).ethereum?.isPhantom);

  const isOneKeyAvailable =
    typeof window !== "undefined" &&
    (!!(window as any).$onekey?.ethereum ||
      !!(window as any).ethereum?.isOneKey);

  // Find connectors for supported wallets
  const injectedConnector = connectors.find((c) => c.id === "injected");

  // Debug logging on component mount and when connectors change
  useEffect(() => {
    console.log("[Phantom Debug] Component mounted/updated");
    console.log(
      "[Phantom Debug] Window object:",
      typeof window !== "undefined" ? "available" : "undefined",
    );
    console.log("[Phantom Debug] window.phantom:", (window as any).phantom);
    console.log("[Phantom Debug] window.ethereum:", (window as any).ethereum);
    console.log(
      "[Phantom Debug] window.ethereum?.isMetaMask:",
      (window as any).ethereum?.isMetaMask,
    );
    console.log(
      "[Phantom Debug] window.ethereum?.isPhantom:",
      (window as any).ethereum?.isPhantom,
    );
    console.log(
      "[Phantom Debug] window.ethereum?.isOneKey:",
      (window as any).ethereum?.isOneKey,
    );
    console.log("[Phantom Debug] isPhantomAvailable:", isPhantomAvailable);
    console.log("[Phantom Debug] All connectors:", connectors);
    console.log("[Phantom Debug] Injected connector:", injectedConnector);
    console.log(
      "[Phantom Debug] Injected connector ID:",
      injectedConnector?.id,
    );
    console.log(
      "[Phantom Debug] Injected connector name:",
      injectedConnector?.name,
    );
    console.log("[Phantom Debug] isConnecting:", isConnecting);
  }, [connectors, injectedConnector, isPhantomAvailable, isConnecting]);

  // Determine which wallet is connected (moved before debug useEffect that uses these)
  // Use fallback: if wagmi has an address that matches ownerAddress, consider it connected
  // This handles cases where wagmi state hasn't updated yet but we have the address
  const hasMatchingAddress =
    ownerAddress &&
    wagmiAddress &&
    ownerAddress.toLowerCase() === wagmiAddress.toLowerCase();

  // Check wallet type from detection
  const detectedWallet =
    typeof window !== "undefined"
      ? (window as any).phantom?.ethereum || (window as any).ethereum?.isPhantom
        ? "Phantom"
        : (window as any).$onekey?.ethereum ||
            (window as any).ethereum?.isOneKey
          ? "OneKey"
          : (window as any).ethereum?.isMetaMask
            ? "MetaMask"
            : null
      : null;

  // Enhanced connection check: use hook result OR fallback checks
  // Fallback 1: wagmi is connected and address matches
  // Fallback 2: ownerAddress is set and wallet is detected (temporary state until wagmi updates)
  const isMetaMaskConnectedEnhanced =
    isMetaMaskConnected ||
    (wagmiIsConnected && hasMatchingAddress && detectedWallet === "MetaMask") ||
    (ownerAddress && detectedWallet === "MetaMask" && isMetaMaskAvailable);
  const isPhantomConnectedEnhanced =
    isPhantomConnected ||
    (wagmiIsConnected && hasMatchingAddress && detectedWallet === "Phantom") ||
    (ownerAddress && detectedWallet === "Phantom" && isPhantomAvailable);
  const isOneKeyConnectedEnhanced =
    isOneKeyConnected ||
    (wagmiIsConnected && hasMatchingAddress && detectedWallet === "OneKey") ||
    (ownerAddress && detectedWallet === "OneKey" && isOneKeyAvailable);

  const connectedWallet = isMetaMaskConnectedEnhanced
    ? "MetaMask"
    : isPhantomConnectedEnhanced
      ? "Phantom"
      : isOneKeyConnectedEnhanced
        ? "OneKey"
        : null;

  const isWalletConnected =
    isMetaMaskConnectedEnhanced ||
    isPhantomConnectedEnhanced ||
    isOneKeyConnectedEnhanced;
  // Wallet is connected if we have addresses available (even if not selected yet)
  const hasWalletConnection =
    availableAddresses.length > 0 || isWalletConnected;
  const isWalletReady = isWalletConnected && ownerAddress && agentAddress;

  // Clear addresses when wallet disconnects
  useEffect(() => {
    if (!wagmiIsConnected && availableAddresses.length > 0) {
      setAvailableAddresses([]);
      if (ownerAddress) {
        setOwnerAddress("");
      }
    }
  }, [wagmiIsConnected, availableAddresses.length, ownerAddress]);

  // Debug: Log connection state
  useEffect(() => {
    console.log("[Connection Debug] wagmiIsConnected:", wagmiIsConnected);
    console.log("[Connection Debug] wagmiAddress:", wagmiAddress);
    console.log("[Connection Debug] ownerAddress:", ownerAddress);
    console.log(
      "[Connection Debug] isPhantomConnected (hook):",
      isPhantomConnected,
    );
    console.log(
      "[Connection Debug] isMetaMaskConnected (hook):",
      isMetaMaskConnected,
    );
    console.log(
      "[Connection Debug] isOneKeyConnected (hook):",
      isOneKeyConnected,
    );
    console.log("[Connection Debug] detectedWallet:", detectedWallet);
    console.log("[Connection Debug] hasMatchingAddress:", hasMatchingAddress);
    console.log(
      "[Connection Debug] isPhantomConnectedEnhanced:",
      isPhantomConnectedEnhanced,
    );
    console.log("[Connection Debug] isWalletConnected:", isWalletConnected);
    console.log("[Connection Debug] availableAddresses:", availableAddresses);
  }, [
    wagmiIsConnected,
    wagmiAddress,
    ownerAddress,
    isPhantomConnected,
    isMetaMaskConnected,
    isOneKeyConnected,
    detectedWallet,
    hasMatchingAddress,
    isPhantomConnectedEnhanced,
    isWalletConnected,
    availableAddresses,
  ]);

  const handleConnectMetaMask = async () => {
    if (!isMetaMaskAvailable) {
      alert(
        "MetaMask wallet is not installed. Please install MetaMask wallet extension.",
      );
      return;
    }

    try {
      // Request accounts from MetaMask first
      const ethereum = (window as any).ethereum;
      if (ethereum) {
        const accounts = await ethereum.request({
          method: "eth_requestAccounts",
        });

        // Store all available addresses
        if (accounts && accounts.length > 0) {
          setAvailableAddresses(accounts);
          // If only one address, auto-select it
          if (accounts.length === 1) {
            setOwnerAddress(accounts[0]);
          }
        }
      }

      // Connect via wagmi
      if (injectedConnector) {
        await connect({ connector: injectedConnector });
        // Wait for wagmi to update
        await new Promise((resolve) => setTimeout(resolve, 300));
      } else {
        alert(
          "MetaMask connector not found. Please ensure MetaMask wallet is installed.",
        );
      }
    } catch (error: any) {
      if (error.code === 4001) {
        alert("User rejected the connection request.");
      } else {
        console.error("Error connecting to MetaMask:", error);
        alert(
          `Failed to connect to MetaMask: ${error.message || "Unknown error"}`,
        );
      }
    }
  };

  const handleConnectPhantom = async () => {
    console.log("[Phantom Debug] Starting connection process...");
    console.log("[Phantom Debug] isPhantomAvailable:", isPhantomAvailable);
    console.log("[Phantom Debug] window.phantom:", (window as any).phantom);
    console.log("[Phantom Debug] window.ethereum:", (window as any).ethereum);
    console.log(
      "[Phantom Debug] window.ethereum?.isPhantom:",
      (window as any).ethereum?.isPhantom,
    );

    if (!isPhantomAvailable) {
      console.error("[Phantom Debug] Phantom wallet not available");
      alert(
        "Phantom wallet is not installed. Please install Phantom wallet extension.",
      );
      return;
    }

    try {
      // Phantom wallet can expose its provider in different ways:
      // 1. window.phantom.ethereum (most common)
      // 2. window.phantom itself (some versions)
      // 3. window.ethereum with isPhantom flag
      let phantomProvider = (window as any).phantom?.ethereum;
      console.log(
        "[Phantom Debug] phantomProvider (window.phantom.ethereum):",
        phantomProvider,
      );

      // Check if window.phantom itself is the provider (has request method)
      if (!phantomProvider && (window as any).phantom) {
        const phantom = (window as any).phantom;
        if (typeof phantom.request === "function") {
          phantomProvider = phantom;
          console.log("[Phantom Debug] Using window.phantom as provider");
        }
      }

      // Fallback to window.ethereum if it's Phantom
      if (!phantomProvider && (window as any).ethereum?.isPhantom) {
        phantomProvider = (window as any).ethereum;
        console.log(
          "[Phantom Debug] Using window.ethereum as provider (isPhantom=true)",
        );
      }

      if (!phantomProvider) {
        console.error("[Phantom Debug] No valid Phantom provider found");
        console.error("[Phantom Debug] Available providers:", {
          hasWindowPhantom: !!(window as any).phantom,
          hasPhantomEthereum: !!(window as any).phantom?.ethereum,
          phantomHasRequest:
            typeof (window as any).phantom?.request === "function",
          hasWindowEthereum: !!(window as any).ethereum,
          ethereumIsPhantom: (window as any).ethereum?.isPhantom,
          ethereumIsMetaMask: (window as any).ethereum?.isMetaMask,
        });
        alert(
          "Phantom wallet provider not found. Please ensure Phantom wallet is installed and enabled.",
        );
        return;
      }

      console.log("[Phantom Debug] Using provider, requesting accounts...");
      // Request account access from Phantom directly
      // This will trigger the Phantom wallet popup
      const accounts = await phantomProvider.request({
        method: "eth_requestAccounts",
      });
      console.log("[Phantom Debug] Accounts received:", accounts);

      // Store all available addresses
      if (accounts && accounts.length > 0) {
        setAvailableAddresses(accounts);
        console.log("[Phantom Debug] Available addresses:", accounts);

        // If only one address, auto-select it
        if (accounts.length === 1) {
          console.log(
            "[Phantom Debug] Auto-selecting single address:",
            accounts[0],
          );
          setOwnerAddress(accounts[0]);
        }
      }

      // After Phantom is connected, connect via wagmi
      // Small delay to ensure Phantom has processed the connection
      console.log("[Phantom Debug] Waiting 100ms before wagmi connection...");
      await new Promise((resolve) => setTimeout(resolve, 100));

      console.log("[Phantom Debug] injectedConnector:", injectedConnector);
      console.log("[Phantom Debug] All connectors:", connectors);

      if (injectedConnector) {
        console.log(
          "[Phantom Debug] Connecting via wagmi injected connector...",
        );
        await connect({ connector: injectedConnector });
        console.log("[Phantom Debug] Wagmi connect() called");

        // Wait a bit more for wagmi to update its state
        console.log("[Phantom Debug] Waiting for wagmi state update...");
        await new Promise((resolve) => setTimeout(resolve, 300));
        console.log("[Phantom Debug] Connection should be complete");
      } else {
        console.error("[Phantom Debug] Injected connector not found");
        alert(
          "Phantom connector not found. Please ensure Phantom wallet is installed.",
        );
      }
    } catch (error: any) {
      console.error("[Phantom Debug] Error caught:", error);
      console.error("[Phantom Debug] Error code:", error.code);
      console.error("[Phantom Debug] Error message:", error.message);
      console.error("[Phantom Debug] Error stack:", error.stack);

      if (error.code === 4001) {
        console.log("[Phantom Debug] User rejected the connection");
        alert("User rejected the connection request.");
      } else {
        console.error("[Phantom Debug] Unknown error during connection");
        alert(
          `Failed to connect to Phantom: ${error.message || "Unknown error"}`,
        );
      }
    }
  };

  const handleConnectOneKey = async () => {
    if (!isOneKeyAvailable) {
      alert(
        "OneKey wallet is not installed. Please install OneKey wallet extension.",
      );
      return;
    }

    try {
      // OneKey wallet exposes its provider at window.$onekey.ethereum
      // We need to request connection directly from OneKey first
      const oneKeyProvider =
        (window as any).$onekey?.ethereum || (window as any).ethereum;

      if (!oneKeyProvider) {
        alert(
          "OneKey wallet provider not found. Please ensure OneKey wallet is installed and enabled.",
        );
        return;
      }

      // Request account access from OneKey directly
      // This will trigger the OneKey wallet popup
      const accounts = await oneKeyProvider.request({
        method: "eth_requestAccounts",
      });

      // Store all available addresses
      if (accounts && accounts.length > 0) {
        setAvailableAddresses(accounts);
        // If only one address, auto-select it
        if (accounts.length === 1) {
          setOwnerAddress(accounts[0]);
        }
      }

      // After OneKey is connected, connect via wagmi
      if (injectedConnector) {
        await connect({ connector: injectedConnector });
        // Wait for wagmi to update
        await new Promise((resolve) => setTimeout(resolve, 300));
      } else {
        alert(
          "OneKey connector not found. Please ensure OneKey wallet is installed.",
        );
      }
    } catch (error: any) {
      if (error.code === 4001) {
        alert("User rejected the connection request.");
      } else {
        console.error("Error connecting to OneKey:", error);
        alert(
          `Failed to connect to OneKey: ${error.message || "Unknown error"}`,
        );
      }
    }
  };

  return (
    <div className="component">
      <h3>Agent Delegation</h3>
      <p>Approve an agent private key to act on behalf of the owner wallet</p>

      {/* Step 1: Connect Wallet */}
      <div
        className={`agent-delegation-step ${hasWalletConnection ? "connected" : ""}`}
      >
        <h4>
          Step 1: Connect Wallet {isWalletConnected && ownerAddress && "✓"}
        </h4>

        {!hasWalletConnection ? (
          <div className="agent-delegation-wallet-buttons">
            {isMetaMaskAvailable && (
              <button
                className="button"
                onClick={handleConnectMetaMask}
                disabled={isConnecting || !injectedConnector}
              >
                {isConnecting ? "Connecting..." : "Connect MetaMask"}
              </button>
            )}

            {isPhantomAvailable && (
              <button
                className="button"
                onClick={handleConnectPhantom}
                disabled={isConnecting || !injectedConnector}
              >
                {isConnecting ? "Connecting..." : "Connect Phantom"}
              </button>
            )}

            {isOneKeyAvailable && (
              <button
                className="button"
                onClick={handleConnectOneKey}
                disabled={isConnecting || !injectedConnector}
              >
                {isConnecting ? "Connecting..." : "Connect OneKey"}
              </button>
            )}

            {!isMetaMaskAvailable &&
              !isPhantomAvailable &&
              !isOneKeyAvailable && (
                <p className="agent-delegation-error-message">
                  No supported wallet detected. Please install MetaMask,
                  Phantom, or OneKey wallet.
                </p>
              )}
          </div>
        ) : availableAddresses.length > 0 && !ownerAddress ? (
          <div>
            <p style={{ marginBottom: "1rem" }}>
              <strong>Connected:</strong> {connectedWallet || "Wallet"}
            </p>
            <div className="form-group">
              <label htmlFor="addressSelect">Select an address to use:</label>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                }}
              >
                {availableAddresses.map((address) => (
                  <button
                    key={address}
                    className={`button ${ownerAddress?.toLowerCase() === address.toLowerCase() ? "" : "secondary"}`}
                    onClick={() => setOwnerAddress(address)}
                    style={{
                      textAlign: "left",
                      justifyContent: "flex-start",
                      fontFamily: "monospace",
                      fontSize: "0.9rem",
                    }}
                  >
                    {address}
                  </button>
                ))}
              </div>
            </div>
            <button
              className="button secondary"
              onClick={() => {
                disconnect();
                setAvailableAddresses([]);
                setOwnerAddress("");
              }}
              style={{ marginTop: "1rem" }}
            >
              Disconnect
            </button>
          </div>
        ) : (
          <div className="agent-delegation-connected-info">
            <div>
              <strong>Connected:</strong> {connectedWallet}
              {ownerAddress && (
                <div className="agent-delegation-address">
                  Address: {ownerAddress}
                </div>
              )}
            </div>
            <button
              className="button secondary"
              onClick={() => {
                disconnect();
                setAvailableAddresses([]);
                setOwnerAddress("");
              }}
            >
              Disconnect
            </button>
          </div>
        )}
      </div>

      {/* Step 2: Configure Agent Delegation (only show if wallet is connected and address is selected) */}
      {isWalletConnected && ownerAddress ? (
        <div
          className={`agent-delegation-step ${isWalletReady ? "connected" : ""}`}
        >
          <h4>Step 2: Configure Agent Delegation {isWalletReady && "✓"}</h4>

          <div className="form-group">
            <label htmlFor="ownerAddress">Owner Address:</label>
            <input
              id="ownerAddress"
              type="text"
              value={ownerAddress}
              readOnly
              style={{
                fontFamily: "monospace",
                fontSize: "0.9rem",
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                cursor: "not-allowed",
              }}
            />
            <small className="agent-delegation-small-text">
              This address is from your connected {connectedWallet} wallet.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="agentAddress">Agent Address:</label>
            <div className="agent-delegation-input-group">
              <input
                id="agentAddress"
                type="text"
                value={agentAddress}
                onChange={(e) => setAgentAddress(e.target.value)}
                placeholder="0x..."
                className="agent-delegation-input-flex"
              />
              <button
                className="button secondary agent-delegation-button-compact"
                onClick={generateAgentKeypair}
                disabled={loading || !isReady}
              >
                Generate Keypair
              </button>
            </div>
          </div>

          {agentPrivateKey && (
            <div className="form-group">
              <label htmlFor="agentPrivateKey">
                Agent Private Key (save securely):
              </label>
              <input
                id="agentPrivateKey"
                type="text"
                value={agentPrivateKey}
                readOnly
                className="agent-delegation-private-key"
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="agentName">Agent Name (optional):</label>
            <input
              id="agentName"
              type="text"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              placeholder="enter you agent name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="expiresInDays">Expires In Days:</label>
            <input
              id="expiresInDays"
              type="number"
              value={expiresInDays}
              onChange={(e) =>
                setExpiresInDays(parseInt(e.target.value) || 180)
              }
              min="1"
              max="365"
            />
          </div>

          <div className="form-group">
            <label htmlFor="permissions">Permissions (comma-separated):</label>
            <input
              id="permissions"
              type="text"
              value={permissions}
              onChange={(e) => setPermissions(e.target.value)}
              placeholder="clob::submit_order,clob::cancel_order"
            />
            <small className="agent-delegation-small-text">
              Example: clob::submit_order, clob::cancel_order,
              clob::modify_order
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="nonce">Nonce:</label>
            <input
              id="nonce"
              type="text"
              value={nonce}
              onChange={(e) => setNonce(e.target.value)}
              placeholder={Date.now().toString()}
            />
          </div>
        </div>
      ) : (
        <div className="agent-delegation-step disabled">
          <h4>Step 2: Configure Agent Delegation</h4>
          <p className="agent-delegation-disabled-text">
            {hasWalletConnection && !ownerAddress
              ? "Please select an address in Step 1 to continue."
              : "Please connect a wallet in Step 1 to continue."}
          </p>
        </div>
      )}

      {/* Step 3: Prepare and Sign (only show if wallet is ready) */}
      {isWalletReady ? (
        <div className="agent-delegation-step">
          <h4>Step 3: Prepare and Sign Agent Approval</h4>

          <div className="agent-delegation-button-group">
            {isMetaMaskConnected && (
              <button
                className="button"
                onClick={handleSignAgentApproval}
                disabled={isSigning || !ownerAddress || !agentAddress}
              >
                {isSigning
                  ? "Signing with MetaMask..."
                  : "Sign Agent Approval (MetaMask)"}
              </button>
            )}

            {isPhantomConnected && (
              <button
                className="button"
                onClick={handleSignAgentApproval}
                disabled={isSigning || !ownerAddress || !agentAddress}
              >
                {isSigning
                  ? "Signing with Phantom..."
                  : "Sign Agent Approval (Phantom)"}
              </button>
            )}

            {isOneKeyConnected && (
              <button
                className="button"
                onClick={handleSignAgentApproval}
                disabled={isSigning || !ownerAddress || !agentAddress}
              >
                {isSigning
                  ? "Signing with OneKey..."
                  : "Sign Agent Approval (OneKey)"}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="agent-delegation-step disabled">
          <h4>Step 3: Prepare and Sign Agent Approval</h4>
          <p className="agent-delegation-disabled-text">
            Please complete Step 1 (connect wallet) and Step 2 (configure
            addresses) to continue.
          </p>
        </div>
      )}

      {(result || signingResult) && (
        <div
          className={`output ${(result || signingResult)?.success ? "success" : "error"}`}
        >
          {(result || signingResult)?.success ? (
            <>
              {(result || signingResult)?.signature && (
                <>
                  <strong>Agent Approval Signature:</strong>
                  <br />
                  {(result || signingResult)?.signature}
                  <br />
                  <br />
                </>
              )}
              {(result || signingResult)?.typedData && (
                <>
                  <strong>EIP-712 Typed Data:</strong>
                  <br />
                  <pre className="agent-delegation-pre">
                    {JSON.stringify(
                      (result || signingResult)?.typedData,
                      null,
                      2,
                    )}
                  </pre>
                  <br />
                  <strong>Usage:</strong>
                  <br />
                  Signature received from wallet. The typed data structure is
                  shown above.
                </>
              )}
            </>
          ) : (
            <>
              <strong>Error:</strong>
              <br />
              {(result || signingResult)?.error}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default AgentDelegation;
